import React, { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
  useParams,
} from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

import LoginScreen from "@/components/LoginScreen";
import DashboardScreen from "@/components/DashboardScreen";
import AssistantRedirect from "@/components/assistant/AssistantRedirect";
import FormulaireClient from "@/components/formulaire/FormulaireClient";
import MyRequests from "@/components/agent/MyRequests";
import Deliveries from "@/components/agent/Deliveries";
import ClientPage from "@/components/client/ClientPage";
import AppShell from "@/components/layout/AppShell";
import { Toaster } from "@/components/ui/sonner";

const BD_ALLOWED_EMAILS = [
  "undeuxb@gmail.com",
  "v.tamisier@gmail.com",
  "v.abrial.pro@gmail.com",
  "quentin.tousart@webpulser.com",
];

// Miroir de EMAIL_TO_PERSONA / TEAM_PERSONAS cote backend (agent/contexts.py) :
// c'est cette liste-la que `is_team_email` applique sur /api/agent/deliveries.
// Elle coincide aujourd'hui avec BD_ALLOWED_EMAILS, mais les deux ne disent pas
// la meme chose : ouvrir la BD a quelqu'un ne doit pas lui afficher un bouton
// qui repondra 403.
const TEAM_EMAILS = [
  "v.tamisier@gmail.com",
  "v.abrial.pro@gmail.com",
  "undeuxb@gmail.com",
  "quentin.tousart@webpulser.com",
];

const ALLOWED_MINISITE_EMAILS = [
  "undeuxb@gmail.com",
  "v.tamisier@gmail.com",
  "v.abrial.pro@gmail.com",
  "quentin.tousart@webpulser.com",
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function RedirectToClient({ tab }) {
  const { clientId } = useParams();
  return <Navigate to={`/clients/${clientId}/${tab}`} replace />;
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('autobio_token'));
  // sessionRestoring covers the async axios /auth/me call below. While true,
  // route guards must NOT render the login on a refresh with a valid token.
  const [sessionRestoring, setSessionRestoring] = useState(
    () => !!localStorage.getItem('autobio_token')
  );

  // Axios interceptor for JWT
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const savedToken = localStorage.getItem('autobio_token');
      if (savedToken) {
        config.headers.Authorization = `Bearer ${savedToken}`;
      }
      return config;
    });

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('autobio_token');
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Restore authentication from localStorage on mount.
  useEffect(() => {
    localStorage.removeItem('autobio_sessionId');
    const savedToken = localStorage.getItem('autobio_token');
    if (!savedToken) {
      setSessionRestoring(false);
      return;
    }

    const restoreSession = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data.user);
      } catch (error) {
        console.error('Failed to restore session:', error);
        localStorage.removeItem('autobio_token');
        setToken(null);
        setUser(null);
      } finally {
        setSessionRestoring(false);
      }
    };

    restoreSession();
  }, []);

  const handleLogin = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data.success) {
        localStorage.setItem('autobio_token', response.data.token);
        setToken(response.data.token);
        setUser(response.data.user);
        toast.success("Connexion réussie");
        return true;
      }

      toast.error(response.data.message || "Identifiants incorrects");
      return false;
    } catch (error) {
      toast.error("Erreur de connexion au serveur");
      return false;
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('autobio_token');
    setToken(null);
    setUser(null);
  }, []);

  const userEmail = (user?.email || "").toLowerCase();
  const canAccessBD = BD_ALLOWED_EMAILS.includes(userEmail);
  const isTeam = TEAM_EMAILS.includes(userEmail);
  const canAccessMinisite = ALLOWED_MINISITE_EMAILS.includes(userEmail);

  const requireAuth = useCallback((element) => {
    if (sessionRestoring) return <div className="min-h-screen" />;
    if (!user) {
      return (
        <>
          <LoginScreen onLogin={handleLogin} />
          <Toaster position="top-right" richColors />
        </>
      );
    }
    return element;
  }, [handleLogin, sessionRestoring, user]);

  const router = useMemo(() => createBrowserRouter(createRoutesFromElements(
    <>
      <Route path="/formulaire/:token" element={<FormulaireClient />} />
      <Route
        element={requireAuth(
          <AppShell user={user} isTeam={isTeam} onLogout={handleLogout} token={token} />
        )}
      >
        <Route path="/" element={<DashboardScreen user={user} />} />
        <Route
          path="/clients/nouveau/:tab?"
          element={(
            <ClientPage
              token={token}
              canAccessBD={canAccessBD}
              canAccessMinisite={canAccessMinisite}
              userEmail={userEmail}
            />
          )}
        />
        <Route
          path="/clients/:clientId/:tab?"
          element={(
            <ClientPage
              token={token}
              canAccessBD={canAccessBD}
              canAccessMinisite={canAccessMinisite}
              userEmail={userEmail}
            />
          )}
        />
        <Route path="/bd" element={<Navigate to="/" replace />} />
        <Route path="/bd/:clientId" element={<RedirectToClient tab="bd" />} />
        <Route path="/minisite" element={<Navigate to="/" replace />} />
        <Route path="/minisite/:clientId" element={<RedirectToClient tab="minisite" />} />
        <Route path="/mes-demandes" element={<MyRequests token={token} />} />
        <Route
          path="/nouveautes"
          element={isTeam ? <Deliveries token={token} /> : <Navigate to="/" replace />}
        />
        <Route path="/assistant" element={<AssistantRedirect />} />
        <Route path="/assistant/:contextName" element={<AssistantRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </>
  )), [
    canAccessBD,
    canAccessMinisite,
    handleLogout,
    isTeam,
    requireAuth,
    token,
    user,
    userEmail,
  ]);

  return <RouterProvider router={router} />;
}

export default App;
